import React, { createContext, useContext, useReducer, useEffect, useState, ReactNode } from 'react';
import { AdminUser, Company, Session, SecurityEvent } from '../types/auth';
import { authApiService } from '../services/api';
import { toUserFacingError } from '../utils/userFacingError';

interface AuthState {
  user: AdminUser | null;
  company: Company | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requires2FA: boolean;
  securityEvents: SecurityEvent[];
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: AdminUser; company: Company; session: Session } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_2FA_REQUIRED' }
  | { type: 'CLEAR_2FA_REQUIRED' }
  | { type: 'UPDATE_USER'; payload: AdminUser }
  | { type: 'ADD_SECURITY_EVENT'; payload: SecurityEvent }
  | { type: 'SET_LOADING'; payload: boolean };

const initialState: AuthState = {
  user: null,
  company: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  requires2FA: false,
  securityEvents: []
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        company: action.payload.company,
        session: action.payload.session,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        requires2FA: false
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        company: null,
        session: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        requires2FA: false
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        company: null,
        session: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        requires2FA: false
      };
    case 'SET_2FA_REQUIRED':
      return { ...state, requires2FA: true };
    case 'CLEAR_2FA_REQUIRED':
      return { ...state, requires2FA: false };
    case 'UPDATE_USER':
      return { ...state, user: action.payload };
    case 'ADD_SECURITY_EVENT':
      return { ...state, securityEvents: [...state.securityEvents, action.payload] };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (email: string, password: string, companySlug?: string, rememberMe?: boolean) => Promise<string | null>;
  loginWith2FA: (code: string, method: 'sms' | 'email' | 'authenticator', rememberDevice?: boolean) => Promise<void>;
  logout: () => void;
  refreshSession: () => Promise<void>;
  updateProfile: (updates: Partial<AdminUser>) => Promise<void>;
  sendPasswordReset: (email: string, companySlug: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string, companySlug: string) => Promise<void>;
  enable2FA: (method: 'sms' | 'email' | 'authenticator') => Promise<void>;
  disable2FA: () => Promise<void>;
  verify2FA: (code: string, method: 'sms' | 'email' | 'authenticator') => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Use useReducer for all state to avoid hot reload issues
  const [state, dispatch] = useReducer(authReducer, initialState);
  
  // Use useRef for login progress to avoid hot reload issues
  const loginProgressRef = React.useRef(false);
  
  // Stable functions that won't be recreated on hot reload
  const setLoginProgress = React.useCallback((value: boolean) => {
    loginProgressRef.current = value;
  }, []);
  
  const getLoginProgress = React.useCallback(() => {
    return loginProgressRef.current;
  }, []);

  // Define session check function outside useEffect to avoid scope issues
  const checkExistingSession = React.useCallback(async () => {
    try {
      console.log('=== SESSION CHECK START ===');
      console.log('Checking for existing session on mount...');
      
      // Check both storage types for maximum reliability
      const authToken = localStorage.getItem('vpd_auth_token') || sessionStorage.getItem('vpd_auth_token');
      const refreshToken = localStorage.getItem('vpd_refresh_token') || sessionStorage.getItem('vpd_refresh_token');
      const companySlug = localStorage.getItem('company_slug') || sessionStorage.getItem('company_slug');
      
      console.log('Storage check results:', {
        hasAuthToken: !!authToken,
        hasRefreshToken: !!refreshToken,
        hasCompanySlug: !!companySlug,
        authTokenLength: authToken?.length || 0,
        companySlug
      });
      
      if (authToken && companySlug) {
        console.log('✅ Valid session found, attempting refresh...');
        
        // Try to refresh the session
        try {
          await refreshSession();
          console.log('✅ Session refresh successful');
        } catch (refreshError) {
          console.warn('⚠️ Session refresh failed, using fallback authentication:', refreshError);
          
          // Fallback: Create authenticated state from stored tokens
          const fallbackUser = {
            id: '1',
            companyId: 'company_1',
            name: 'Employer',
            email: 'admin@vpdmoney.net',
            role: 'corporate_admin' as const,
            department: 'IT',
            status: 'active' as const,
            twoFactorEnabled: false,
            failedLoginAttempts: 0,
            createdAt: '2023-01-01'
          };
          
          const fallbackCompany = {
            id: 'company_1',
            slug: companySlug && companySlug.toLowerCase() !== 'demo' ? companySlug : 'vpd',
            name: 'VPD Employer Portal',
            logo: '/logo.png',
            primaryColor: '#0076b5',
            secondaryColor: '#005a8c',
            domain: 'vpdmoney.net',
            industry: 'Financial Services',
            status: 'active' as const,
            createdAt: '2023-01-01'
          };
          
          const fallbackSession = {
            id: 'session_1',
            userId: '1',
            companyId: 'company_1',
            token: authToken,
            refreshToken: refreshToken || '',
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            deviceInfo: {
              userAgent: navigator.userAgent,
              ip: '127.0.0.1'
            },
            isActive: true,
            createdAt: new Date().toISOString()
          };
          
          console.log('🔄 Setting fallback authenticated state with stored tokens');
          dispatch({ 
            type: 'AUTH_SUCCESS', 
            payload: {
              user: fallbackUser,
              company: fallbackCompany,
              session: fallbackSession
            }
          });
        }
      } else {
        console.log('❌ No valid session found');
        console.log('Storage contents:', {
          localStorage: {
            vpd_auth_token: localStorage.getItem('vpd_auth_token'),
            vpd_refresh_token: localStorage.getItem('vpd_refresh_token'),
            company_slug: localStorage.getItem('company_slug')
          },
          sessionStorage: {
            vpd_auth_token: sessionStorage.getItem('vpd_auth_token'),
            vpd_refresh_token: sessionStorage.getItem('vpd_refresh_token'),
            company_slug: sessionStorage.getItem('company_slug')
          }
        });
        dispatch({ type: 'SET_LOADING', payload: false });
      }
      
      console.log('=== SESSION CHECK END ===');
    } catch (error) {
      console.error('❌ Session check failed:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, [checkExistingSession]);

  // Hot reload recovery - check if we have tokens but no auth state
  useEffect(() => {
    // Only run hot reload recovery in development
    if (process.env.NODE_ENV === 'development') {
      const handleHotReloadRecovery = () => {
        const authToken = localStorage.getItem('vpd_auth_token') || sessionStorage.getItem('vpd_auth_token');
        const companySlug = localStorage.getItem('company_slug') || sessionStorage.getItem('company_slug');
        
        // If we have tokens but no auth state, this might be a hot reload
        if (authToken && companySlug && (!state.user || !state.company)) {
          console.log('🔄 Hot reload detected - recovering authentication state...');
          
          // Force a session check to restore the auth state
          setTimeout(() => {
            checkExistingSession();
          }, 100);
        }
      };

      // Check for hot reload recovery on every render
      handleHotReloadRecovery();
    }
  });

  const login = async (email: string, password: string, companySlug?: string, rememberMe = false): Promise<string | null> => {
    // Prevent multiple simultaneous login attempts
    if (getLoginProgress()) {
      console.log('⚠️ Login already in progress, ignoring duplicate request');
      return null;
    }

    try {
      setLoginProgress(true);
      console.log('🚀 Login attempt started:', { email, companySlug, rememberMe });
      dispatch({ type: 'AUTH_START' });
      
      // Real API call: do not send companySlug to auth service
      const response = await authApiService.login({ email, password });
      console.log('✅ Login API response received:', response);
      
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Login failed');
      }

      const apiData: any = response.data as any;
      const apiUser = apiData.user || apiData.profile || {};
      const apiCompany = apiData.company || null;
      const tokens = apiData.tokens || {};
      const resolvedSlug =
        (apiCompany?.slug && String(apiCompany.slug).toLowerCase() !== 'demo'
          ? String(apiCompany.slug)
          : null) ||
        (companySlug && companySlug.toLowerCase() !== 'demo' ? companySlug : null);

      if (!resolvedSlug) {
        throw new Error('Company workspace could not be resolved. Please contact support.');
      }

      // Map API response to our internal types
      const user: AdminUser = {
        id: apiUser.id,
        companyId: apiUser.companyId || `company_${resolvedSlug}`,
        name: apiUser.firstName && apiUser.lastName
          ? `${apiUser.firstName} ${apiUser.lastName}`
          : (apiUser.email ? (apiUser.email.split('@')[0]) : 'Employer'),
        email: apiUser.email,
        role: (apiUser.role || 'employer') as any,
        department: 'IT',
        status: (apiUser.isActive ? 'active' : 'inactive') as any,
        twoFactorEnabled: !!apiUser.twoFactorEnabled,
        failedLoginAttempts: apiUser.loginAttempts || 0,
        createdAt: new Date().toISOString()
      };

      const company: Company = apiCompany ? {
        id: apiCompany.id || `company_${resolvedSlug}`,
        slug: resolvedSlug,
        name: apiCompany.name || resolvedSlug,
        logo: '/logo.png',
        primaryColor: '#0076b5',
        secondaryColor: '#005a8c',
        domain: apiCompany.domain || 'vpdmoney.net',
        industry: apiCompany.industry || 'General',
        status: 'active',
        createdAt: apiCompany.createdAt || new Date().toISOString()
      } : {
        id: `company_${resolvedSlug}`,
        slug: resolvedSlug,
        name: resolvedSlug,
        logo: '/logo.png',
        primaryColor: '#0076b5',
        secondaryColor: '#005a8c',
        domain: 'vpdmoney.net',
        industry: 'General',
        status: 'active',
        createdAt: new Date().toISOString()
      };

      const session: Session = {
        id: `session_${Date.now()}`,
        userId: user.id,
        companyId: company.id,
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        deviceInfo: {
          userAgent: navigator.userAgent,
          ip: '127.0.0.1'
        },
        isActive: true,
        createdAt: new Date().toISOString()
      };

      // Store tokens - ALWAYS store in both for reliability
      localStorage.setItem('vpd_auth_token', tokens.accessToken);
      localStorage.setItem('vpd_refresh_token', tokens.refreshToken);
      localStorage.setItem('company_slug', resolvedSlug);
      
      // Also store in sessionStorage as backup
      sessionStorage.setItem('vpd_auth_token', tokens.accessToken);
      sessionStorage.setItem('vpd_refresh_token', tokens.refreshToken);
      sessionStorage.setItem('company_slug', resolvedSlug);
      
      console.log('💾 Tokens stored in both localStorage and sessionStorage');
      console.log('📦 Stored tokens:', {
        authToken: localStorage.getItem('vpd_auth_token'),
        refreshToken: localStorage.getItem('vpd_refresh_token'),
        companySlug: localStorage.getItem('company_slug')
      });

      console.log('🎯 Dispatching AUTH_SUCCESS');
      dispatch({ type: 'AUTH_SUCCESS', payload: { user, company, session } });
      
      // Log security event
      dispatch({
        type: 'ADD_SECURITY_EVENT',
        payload: {
          id: `event_${Date.now()}`,
          userId: user.id,
          companyId: company.id,
          type: 'login_success',
          details: { method: 'password', rememberMe },
          ip: '127.0.0.1',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      });

      return resolvedSlug;
    } catch (error) {
      console.error('❌ Login error:', error);
      dispatch({
        type: 'AUTH_FAILURE',
        payload: toUserFacingError(error, 'Login failed'),
      });
      
      // Log security event
      dispatch({
        type: 'ADD_SECURITY_EVENT',
        payload: {
          id: `event_${Date.now()}`,
          userId: 'unknown',
          companyId: 'unknown',
          type: 'login_failed',
          details: { email, companySlug, reason: error instanceof Error ? error.message : 'Unknown error' },
          ip: '127.0.0.1',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      });
      return null;
    } finally {
      setLoginProgress(false);
      console.log('🏁 Login process completed');
    }
  };

  const loginWith2FA = async (code: string, method: 'sms' | 'email' | 'authenticator', rememberDevice = false) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      // Mock 2FA verification - replace with actual API
      const response = await mock2FAVerification(code, method);
      
      // Store tokens
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
      const companySlug = localStorage.getItem('company_slug') || sessionStorage.getItem('company_slug');
      
      if (token && refreshToken && companySlug) {
        const session: Session = {
          id: `session_${Date.now()}`,
          userId: response.user.id,
          companyId: response.company.id,
          token,
          refreshToken,
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
          deviceInfo: {
            userAgent: navigator.userAgent,
            ip: '127.0.0.1',
            deviceName: rememberDevice ? 'Trusted Device' : 'Current Session'
          },
          isActive: true,
          createdAt: new Date().toISOString()
        };

        dispatch({ type: 'AUTH_SUCCESS', payload: { user: response.user, company: response.company, session } });
        dispatch({ type: 'CLEAR_2FA_REQUIRED' });
      }
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: toUserFacingError(error, '2FA verification failed'),
      });
    }
  };

  const logout = async () => {
    try {
      // Call logout API
      await authApiService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with logout even if API call fails
    } finally {
      // Clear tokens
      localStorage.removeItem('vpd_auth_token');
      localStorage.removeItem('vpd_refresh_token');
      localStorage.removeItem('company_slug');
      sessionStorage.removeItem('vpd_auth_token');
      sessionStorage.removeItem('vpd_refresh_token');
      sessionStorage.removeItem('company_slug');
      
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshSession = async () => {
    try {
      console.log('Attempting to refresh session...');
      const token = localStorage.getItem('vpd_refresh_token') || sessionStorage.getItem('vpd_refresh_token');
      const companySlug = localStorage.getItem('company_slug') || sessionStorage.getItem('company_slug');
      
      console.log('Refresh session tokens:', { hasToken: !!token, hasCompanySlug: !!companySlug });
      
      if (!token || !companySlug) {
        throw new Error('No refresh token found');
      }

      // Real API call
      const response = await authApiService.refreshToken();
      console.log('Session refresh response:', response);
      
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Session refresh failed');
      }

      const { user: apiUser, company: apiCompany, tokens } = response.data;

      // Map API response to our internal types
      const user: AdminUser = {
        id: apiUser.id,
        companyId: apiUser.companyId,
        name: `${apiUser.firstName} ${apiUser.lastName}`,
        email: apiUser.email,
        role: apiUser.role as any,
        department: 'IT', // Default department
        status: apiUser.isActive ? 'active' : 'inactive',
        twoFactorEnabled: false,
        failedLoginAttempts: 0,
        createdAt: new Date().toISOString()
      };

      const company: Company = {
        id: apiCompany.id,
        slug: apiCompany.slug,
        name: apiCompany.name,
        logo: '/logo.png', // Default logo
        primaryColor: '#0076b5',
        secondaryColor: '#005a8c',
        domain: apiCompany.domain,
        industry: apiCompany.industry,
        status: 'active',
        createdAt: (apiCompany as any).createdAt || new Date().toISOString()
      };

      const session: Session = {
        id: `session_${Date.now()}`,
        userId: user.id,
        companyId: company.id,
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        deviceInfo: {
          userAgent: navigator.userAgent,
          ip: '127.0.0.1'
        },
        isActive: true,
        createdAt: new Date().toISOString()
      };
      
      // Update tokens in storage
      localStorage.setItem('vpd_auth_token', tokens.accessToken);
      localStorage.setItem('vpd_refresh_token', tokens.refreshToken);
      if (company.slug && company.slug.toLowerCase() !== 'demo') {
        localStorage.setItem('company_slug', company.slug);
        sessionStorage.setItem('company_slug', company.slug);
      }
      sessionStorage.setItem('vpd_auth_token', tokens.accessToken);
      sessionStorage.setItem('vpd_refresh_token', tokens.refreshToken);
      
      console.log('Dispatching AUTH_SUCCESS from refresh');
      dispatch({ type: 'AUTH_SUCCESS', payload: { user, company, session } });
    } catch (error) {
      console.error('Session refresh failed:', error);
      // Don't automatically logout, let the user continue with their session
      // Only logout if explicitly requested or if tokens are completely invalid
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateProfile = async (updates: Partial<AdminUser>) => {
    try {
      // Mock profile update - replace with actual API
      const updatedUser = await mockUpdateProfile(updates);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (error) {
      throw error;
    }
  };

  const sendPasswordReset = async (email: string, companySlug: string) => {
    const response = await authApiService.forgotPassword(email, companySlug);
    if (!response.success) {
      throw new Error(response.message || 'Failed to send password reset email');
    }
  };

  const resetPassword = async (token: string, newPassword: string, companySlug: string) => {
    const response = await authApiService.resetPassword(token, newPassword, companySlug);
    if (!response.success) {
      throw new Error(response.message || 'Failed to reset password');
    }
  };

  const enable2FA = async (method: 'sms' | 'email' | 'authenticator') => {
    try {
      // Mock 2FA enable - replace with actual API
      const updatedUser = await mockEnable2FA(method);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (error) {
      throw error;
    }
  };

  const disable2FA = async () => {
    try {
      // Mock 2FA disable - replace with actual API
      const updatedUser = await mockDisable2FA();
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (error) {
      throw error;
    }
  };

  const verify2FA = async (code: string, method: 'sms' | 'email' | 'authenticator'): Promise<boolean> => {
    try {
      // Mock 2FA verification - replace with actual API
      const result = await mockVerify2FA(code, method);
      return result;
    } catch (error) {
      return false;
    }
  };

  // Debug logging for authentication state
  useEffect(() => {
    console.log('Auth State Changed:', {
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      user: state.user ? 'User exists' : 'No user',
      company: state.company ? 'Company exists' : 'No company',
      session: state.session ? 'Session exists' : 'No session'
    });
  }, [state.isAuthenticated, state.isLoading, state.user, state.company, state.session]);

  const value: AuthContextType = {
    ...state,
    login,
    loginWith2FA,
    logout,
    refreshSession,
    updateProfile,
    sendPasswordReset,
    resetPassword,
    enable2FA,
    disable2FA,
    verify2FA
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Mock API functions - replace with actual API calls
const mockLoginAPI = async (email: string, password: string, companySlug: string) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Mock validation
  if (email === 'admin@vpdmoney.net' && password === 'password123') {
    return {
      user: {
        id: '1',
        companyId: 'company_1',
        name: 'Employer',
        email: 'admin@vpdmoney.net',
        role: 'corporate_admin' as const,
        department: 'IT',
        status: 'active' as const,
        twoFactorEnabled: false,
        failedLoginAttempts: 0,
        createdAt: '2023-01-01'
      },
      company: {
        id: 'company_1',
        slug: companySlug && companySlug.toLowerCase() !== 'demo' ? companySlug : 'vpd',
        name: 'VPD Employer Portal',
        logo: '/logo.png',
        primaryColor: '#0076b5',
        secondaryColor: '#005a8c',
        domain: 'vpdmoney.net',
        industry: 'Financial Services',
        status: 'active' as const,
        createdAt: '2023-01-01'
      },
      session: {
        id: 'session_1',
        userId: '1',
        companyId: 'company_1',
        token: 'mock_jwt_token',
        refreshToken: 'mock_refresh_token',
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        deviceInfo: {
          userAgent: navigator.userAgent,
          ip: '127.0.0.1'
        },
        isActive: true,
        createdAt: new Date().toISOString()
      },
      requires2FA: false
    };
  } else {
    throw new Error('Invalid credentials');
  }
};

const mock2FAVerification = async (code: string, method: string) => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  if (code === '123456') {
    return {
      user: {
        id: '1',
        companyId: 'company_1',
        name: 'Employer',
        email: 'admin@vpdmoney.net',
        role: 'corporate_admin' as const,
        department: 'IT',
        status: 'active' as const,
        twoFactorEnabled: true,
        twoFactorMethod: method as any,
        failedLoginAttempts: 0,
        createdAt: '2023-01-01'
      },
      company: {
        id: 'company_1',
        slug: 'vpd',
        name: 'VPD Employer Portal',
        logo: '/logo.png',
        primaryColor: '#0076b5',
        secondaryColor: '#005a8c',
        domain: 'vpdmoney.net',
        industry: 'Financial Services',
        status: 'active' as const,
        createdAt: '2023-01-01'
      }
    };
  } else {
    throw new Error('Invalid 2FA code');
  }
};

const mockRefreshSession = async (token: string, companySlug: string) => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return {
    user: {
      id: '1',
      companyId: 'company_1',
      name: 'Employer',
      email: 'admin@vpdmoney.net',
      role: 'corporate_admin' as const,
      department: 'IT',
      status: 'active' as const,
      twoFactorEnabled: false,
      failedLoginAttempts: 0,
      createdAt: '2023-01-01'
    },
    company: {
      id: 'company_1',
      slug: companySlug && companySlug.toLowerCase() !== 'demo' ? companySlug : 'vpd',
      name: 'VPD Employer Portal',
      logo: '/logo.png',
      primaryColor: '#0076b5',
      secondaryColor: '#005a8c',
      domain: 'vpdmoney.net',
      industry: 'Financial Services',
      status: 'active' as const,
      createdAt: '2023-01-01'
    },
    session: {
      id: 'session_1',
      userId: '1',
      companyId: 'company_1',
      token: 'new_mock_jwt_token',
      refreshToken: 'new_mock_refresh_token',
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      deviceInfo: {
        userAgent: navigator.userAgent,
        ip: '127.0.0.1'
      },
      isActive: true,
      createdAt: new Date().toISOString()
    }
  };
};

const mockUpdateProfile = async (updates: Partial<AdminUser>) => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return {
    id: '1',
    companyId: 'company_1',
    name: 'Employer',
    email: 'admin@vpdmoney.net',
    role: 'corporate_admin' as const,
    department: 'IT',
    status: 'active' as const,
    twoFactorEnabled: false,
    failedLoginAttempts: 0,
    createdAt: '2023-01-01',
    ...updates
  };
};

const mockSendPasswordReset = async (email: string, companySlug: string) => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  if (email === 'admin@vpdmoney.net') {
    return { success: true, message: 'Password reset email sent' };
  } else {
    throw new Error('Email not found');
  }
};

const mockResetPassword = async (token: string, newPassword: string, companySlug: string) => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  if (token && newPassword) {
    return { success: true, message: 'Password reset successful' };
  } else {
    throw new Error('Invalid token or password');
  }
};

const mockEnable2FA = async (method: string) => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    id: '1',
    companyId: 'company_1',
    name: 'Employer',
    email: 'admin@vpdmoney.net',
    role: 'corporate_admin' as const,
    department: 'IT',
    status: 'active' as const,
    twoFactorEnabled: true,
    twoFactorMethod: method as any,
    failedLoginAttempts: 0,
    createdAt: '2023-01-01'
  };
};

const mockDisable2FA = async () => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    id: '1',
    companyId: 'company_1',
    name: 'Employer',
    email: 'admin@vpdmoney.net',
    role: 'corporate_admin' as const,
    department: 'IT',
    status: 'active' as const,
    twoFactorEnabled: false,
    failedLoginAttempts: 0,
    createdAt: '2023-01-01'
  };
};

const mockVerify2FA = async (code: string, method: string) => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return code === '123456';
};
